import React, { useEffect, useRef, useState } from "react";
import DatePicker from "react-datepicker";
import Select from "react-select";
import moment from "moment";
import "react-datepicker/dist/react-datepicker.css";
import CalendarViewer from "../../calendarViewer/CalendarViewer";

const generateTimeOptions = () => {
  const options = [];
  for (let hour = 0; hour < 24; hour++) {
    for (let minutes = 0; minutes < 60; minutes += 5) {
      const label = moment().hour(hour).minute(minutes).format("h:mm a");
      const value = moment().hour(hour).minute(minutes).format("HH:mm");
      options.push({ value, label });
    }
  }
  return options;
};

const timeOptions = generateTimeOptions();

const EventTiming = ({ data, onChange }) => {
  const endDateManuallyChanged = useRef(false);
  const [hover, setHover] = useState(null);

  const parseDateOnly = (val) => (val ? moment(val, "YYYY-MM-DD") : null);
  const parseTimeOnly = (val) => (val ? moment(val, "HH:mm") : null);

  const startDate = parseDateOnly(data.preferredDate);
  const endDate = parseDateOnly(data.endDate);
  const recurrenceEnd = parseDateOnly(data.recurrenceEnd);

  const startTime = parseTimeOnly(data.startTime);
  const endTime = parseTimeOnly(data.endTime);
  const now = moment();

  const [showCalendar, setShowCalendar] = useState(false);

  const startDateTime =
    startDate && startTime
      ? moment(
          `${startDate.format("YYYY-MM-DD")} ${startTime.format("HH:mm")}`,
          "YYYY-MM-DD HH:mm"
        )
      : null;

  const endDateTime =
    endDate && endTime
      ? moment(
          `${endDate.format("YYYY-MM-DD")} ${endTime.format("HH:mm")}`,
          "YYYY-MM-DD HH:mm"
        )
      : null;

  const errors = {
    startInPast: startDateTime && startDateTime.isBefore(now),
    endBeforeStart:
      startDateTime && endDateTime && endDateTime.isSameOrBefore(startDateTime),
    recurrenceBeforeStart:
      startDateTime &&
      recurrenceEnd &&
      recurrenceEnd.isSameOrBefore(startDateTime),
  };

  useEffect(() => {
    if (data.overnight && startDate) {
      if (!data.endDate || !endDateManuallyChanged.current) {
        const newEnd = startDate.clone().add(1, "day").format("YYYY-MM-DD");
        onChange("endDate", newEnd);
        endDateManuallyChanged.current = false;
      }
    }

    if (data.overnight && startDate && endDateManuallyChanged.current) {
      const prevStart = parseDateOnly(data.preferredDate);
      const prevEnd = parseDateOnly(data.endDate);
      const offset = prevEnd.diff(prevStart, "days");
      const newEnd = startDate.clone().add(offset, "days").format("YYYY-MM-DD");
      onChange("endDate", newEnd);
    }
    // eslint-disable-next-line
  }, [data.preferredDate, data.overnight]);

  const filteredEndTimeOptions = timeOptions.filter((option) => {
    if (!startDateTime || !endDate || data.overnight) return true;
    const endCandidate = moment(
      `${endDate.format("YYYY-MM-DD")} ${option.value}`,
      "YYYY-MM-DD HH:mm"
    );
    return endCandidate.isAfter(startDateTime);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-gray-800">Event Timing</h2>
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="flex-1 space-y-6">
          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <DatePicker
              selected={startDate?.toDate()}
              onChange={(date) => {
                const newStart = moment(date).format("YYYY-MM-DD");
                const prevStart = moment(data.preferredDate, "YYYY-MM-DD");
                const currentEnd = data.endDate
                  ? moment(data.endDate, "YYYY-MM-DD")
                  : null;

                onChange("preferredDate", newStart);

                if (!data.endDate) {
                  onChange("endDate", newStart);
                } else if (currentEnd.isValid() && prevStart.isValid()) {
                  const dayDiff = currentEnd.diff(prevStart, "days");
                  const newEnd = moment(date)
                    .add(dayDiff, "days")
                    .format("YYYY-MM-DD");
                  onChange("endDate", newEnd);
                }
              }}
              dateFormat="dd MMM yyyy"
              minDate={new Date()}
              className="w-full px-4 py-2 border border-gray-300 rounded-md"
            />
            {data.preferredDate && (
              <button
                type="button"
                onClick={() => setShowCalendar(true)}
                className="mt-2 inline-flex items-center px-3 py-1.5 bg-yellow-100 hover:bg-yellow-200 text-yellow-800 border border-yellow-300 text-sm font-medium rounded shadow-sm"
              >
                📅 View Calendar on this Date
              </button>
            )}

            {errors.startInPast && (
              <p className="text-sm text-red-500 mt-1">
                Start date/time cannot be in the past.
              </p>
            )}
          </div>

          {/* Start Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Time
            </label>
            <Select
              options={timeOptions}
              value={
                timeOptions.find((opt) => opt.value === data.startTime) || null
              }
              onChange={(option) => onChange("startTime", option?.value || "")}
              isClearable
              isSearchable
            />
          </div>

          {/* Overnight */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={data.overnight}
              onChange={(e) => {
                const checked = e.target.checked;
                onChange("overnight", checked);
                const start = moment(data.preferredDate, "YYYY-MM-DD");
                const end = moment(data.endDate, "YYYY-MM-DD");

                if (!checked) {
                  onChange("endDate", start.format("YYYY-MM-DD"));
                  const st = data.startTime
                    ? moment(data.startTime, "HH:mm")
                    : null;
                  const et = data.endTime
                    ? moment(data.endTime, "HH:mm")
                    : null;

                  if (
                    st &&
                    et &&
                    end.isSame(start, "day") &&
                    et.isSameOrBefore(st)
                  ) {
                    onChange("endTime", "");
                  }
                }
              }}
            />
            <label className="text-sm text-gray-700">Overnight Event</label>
          </div>

          {/* End Date */}
          {data.overnight && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <DatePicker
                selected={endDate?.toDate()}
                onChange={(date) => {
                  onChange("endDate", moment(date).format("YYYY-MM-DD"));
                  endDateManuallyChanged.current = true;
                }}
                minDate={
                  startDate?.clone().add(1, "day").toDate() || new Date()
                }
                dateFormat="dd MMM yyyy"
                className="w-full px-4 py-2 border border-gray-300 rounded-md"
              />
            </div>
          )}

          {/* End Time */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Time
            </label>
            <Select
              options={filteredEndTimeOptions}
              value={
                timeOptions.find((opt) => opt.value === data.endTime) || null
              }
              onChange={(option) => {
                if (!option?.value) return onChange("endTime", "");
                const endCandidate = moment(
                  `${endDate.format("YYYY-MM-DD")} ${option.value}`,
                  "YYYY-MM-DD HH:mm"
                );
                if (
                  startDateTime &&
                  endCandidate.isSameOrBefore(startDateTime)
                ) {
                  alert("End time must be after the start time.");
                  onChange("endTime", "");
                  return;
                }
                onChange("endTime", option.value);
              }}
              isClearable
              isSearchable
            />
            {errors.endBeforeStart && (
              <p className="text-sm text-red-500 mt-1">
                End date/time must be after the start date/time.
              </p>
            )}
          </div>
        </div>

        <div className="flex-1 space-y-4 pt-2">
          {/* Multi-day */}
          <div
            className="relative flex items-center gap-2 text-sm text-gray-700"
            onMouseEnter={() => setHover("multi")}
            onMouseLeave={() => setHover(null)}
          >
            <input
              type="checkbox"
              id="multiDay"
              checked={data.isMultiDay}
              disabled={data.eventRecurs}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onChange("isMultiDay", isChecked);
                if (isChecked) {
                  onChange("eventRecurs", false);
                  onChange("recurrence", "");
                  onChange("recurrenceEnd", "");
                  onChange("recurOverHolidays", false);
                }
              }}
              className="shrink-0 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <label htmlFor="multiDay" className="cursor-pointer">
              This is a multi-day event
            </label>
            {hover === "multi" && (
              <div className="absolute top-full left-0 mt-1 w-72 text-xs text-gray-600 bg-white border border-gray-300 rounded shadow p-2 z-10">
                Event runs across multiple days (no set pattern). Each day may
                have different locations, students, staff, or activities.
              </div>
            )}
          </div>

          {/* Recurrence */}
          <div
            className="relative flex items-center gap-2 text-sm text-gray-700"
            onMouseEnter={() => setHover("recurring")}
            onMouseLeave={() => setHover(null)}
          >
            <input
              type="checkbox"
              id="eventRecurs"
              checked={data.eventRecurs}
              disabled={data.isMultiDay}
              onChange={(e) => {
                const isChecked = e.target.checked;
                onChange("eventRecurs", isChecked);
                if (isChecked) {
                  onChange("isMultiDay", false);
                } else {
                  onChange("recurrence", "");
                  onChange("recurrenceEnd", "");
                  onChange("recurOverHolidays", false);
                }
              }}
              className="shrink-0 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 disabled:opacity-50"
            />
            <label htmlFor="eventRecurs" className="cursor-pointer">
              This event recurs
            </label>
            {hover === "recurring" && (
              <div className="absolute top-full left-0 mt-1 w-72 text-xs text-gray-600 bg-white border border-gray-300 rounded shadow p-2 z-10">
                The same event occurs on a regular schedule, with identical
                details each time.
              </div>
            )}
          </div>

          {/* Recurring Fields */}
          {data.eventRecurs && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Repeat
                </label>
                <select
                  value={data.recurrence || ""}
                  onChange={(e) => onChange("recurrence", e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                >
                  <option value="">None</option>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="fortnightly">Fortnightly</option>
                  <option value="monthly">Monthly</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Recurrence Ends
                </label>
                <DatePicker
                  selected={recurrenceEnd?.toDate()}
                  onChange={(date) =>
                    onChange("recurrenceEnd", moment(date).format("YYYY-MM-DD"))
                  }
                  minDate={startDate?.toDate() || new Date()}
                  dateFormat="dd MMM yyyy"
                  className="w-full px-4 py-2 border border-gray-300 rounded-md"
                />
                {errors.recurrenceBeforeStart && (
                  <p className="text-sm text-red-500 mt-1">
                    Recurrence end date must be after the event start date.
                  </p>
                )}
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={data.recurOverHolidays || false}
                  onChange={(e) =>
                    onChange("recurOverHolidays", e.target.checked)
                  }
                />
                <label className="text-sm text-gray-700">
                  Include school holidays in recurrence
                </label>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="pt-4 space-y-3">
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={data.dateFlexibility || false}
            onChange={(e) => {
              const isChecked = e.target.checked;
              onChange("dateFlexibility", isChecked);
              if (!isChecked) onChange("dateChangeReason", "");
            }}
          />
          Is it possible this date could change?
        </label>

        {data.dateFlexibility && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason the date might change
            </label>
            <textarea
              rows={3}
              className="w-full border rounded-md px-3 py-2 shadow-sm focus:ring-blue-500 focus:border-blue-500"
              placeholder="E.g. Subject schedule not confirmed, dependent on external provider availability..."
              value={data.dateChangeReason || ""}
              onChange={(e) => onChange("dateChangeReason", e.target.value)}
            />
          </div>
        )}
      </div>
      {showCalendar && (
        <div className="fixed inset-0 z-50 bg-white shadow-xl animate-slide-in overflow-auto">
          <div className="flex flex-col h-full">
            <div className="flex justify-between items-center px-4 py-3 border-b">
              <h2 className="text-lg font-semibold text-gray-800">
                School Calendar
              </h2>
              <button
                onClick={() => setShowCalendar(false)}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Close ✕
              </button>
            </div>
            <div className="flex-1 p-4 overflow-y-auto">
              <CalendarViewer
                preferredDate={data.preferredDate}
                focusEvent={{
                  preferredDate: data.preferredDate,
                  startTime: data.startTime || "08:30",
                  endDate: data.endDate || data.preferredDate,
                  endTime: data.endTime || "09:00",
                  eventName: data.eventName || "Draft Event",
                }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventTiming;
